import { Box, Modal, ModalClose, Sheet } from '@mui/joy'
import { keyframes } from '@mui/system'
import { useCallback, useState } from 'react'
import { PlayIcon } from '@phosphor-icons/react'
import { Fancybox } from '@fancyapps/ui'

import VideoPlayer from '../../../ui/VideoPlayer/VideoPlayer'

import { mediaUrl } from '../../../../utils/mediaUrl'

const CONTROLS_H = 42
const MIN_CTRL_W = 380


const getUrl = (src) => mediaUrl(src)

const popIn = keyframes`
  0% { opacity: 0; transform: scale(0.95); }
  100% { opacity: 1; transform: scale(1); }
`

const fadeOut = keyframes`
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.95); }
`


function Attachments({ attachments, postId = 0 }) {
    const [durations, setDurations] = useState({})
    const [modalOpen, setModalOpen] = useState(false)
    const [isClosing, setIsClosing] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isPip, setIsPip] = useState(false)

    const handlePip = useCallback((active) => {
        setIsPip(active)
    }, [])

    const isVideo = (src) => /\.(mp4|webm|ogg|mov)$/i.test(src)

    const formatTime = (seconds) => {
        if (!Number.isFinite(seconds)) return null
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    const handleLoadedMetadata = (idx, e) => {
        setDurations((prev) => ({ ...prev, [idx]: e.target.duration }))
    }

    const openGallery = useCallback((index, e) => {
        e.preventDefault()
        e.stopPropagation()
        setCurrentIndex(index)
        setModalOpen(true)
        setIsClosing(false)
    }, [])

    const handleClose = useCallback(() => {
        setIsClosing(true)
        setTimeout(() => {
            setModalOpen(false)
            setIsClosing(false)
        }, 300)
    }, [])

    const openFancybox = useCallback((startSrc) => {
        const images = attachments
            .filter((src) => !isVideo(src))
            .map((src) => ({ src: getUrl(src), type: 'image' }))

        const startIndex = images.findIndex((img) => img.src === getUrl(startSrc))

        Fancybox.show(images, {
            startIndex,
            Thumbs: { autoStart: true, type: 'modern' },
            Carousel: { infinite: false },
            Images: { initialSize: 'cover' },
        })
    }, [attachments])

    // useEffect(() => {
    //     if (!attachments?.length) return
    //     const selector = '[data-fancybox="post-gallery"]'
    //     Fancybox.bind(selector, {
    //         Thumbs: { autoStart: true, type: 'modern' },
    //         Carousel: { infinite: false },
    //         Images: { initialSize: "cover" },
    //     })
    //     return () => {
    //         Fancybox.unbind(selector)
    //         Fancybox.close()
    //     }
    // }, [attachments])

    if (!attachments?.length) return null

    const currentSrc = attachments[currentIndex]
    const isCurrentVideo = isVideo(currentSrc)

    return (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 2 }}>
                {attachments.map((src, idx) => {
                    const video = isVideo(src)
                    return (
                        <Box
                            key={idx}
                            onClick={(e) => {
                                e.stopPropagation()
                                if (video) openGallery(idx, e)
                                else openFancybox(src)
                            }}
                            sx={{
                                width: '120px',
                                height: '120px',
                                overflow: 'hidden',
                                borderRadius: '12px',
                                backgroundColor: '#f0f0f0',
                                flexShrink: 0,
                                position: 'relative',
                                cursor: 'pointer',
                            }}
                        >
                            {video ? (
                                <>
                                    <video
                                        src={`${getUrl(src)}#t=0.001`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onLoadedMetadata={(e) => handleLoadedMetadata(idx, e)}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'rgba(0,0,0,0.25)',
                                            transition: '0.12s ease-in-out',
                                            '&:hover': { transform: 'scale(1.08)' },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                backgroundColor: 'rgba(0,0,0,0.65)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#fff',
                                            }}
                                        >
                                            <PlayIcon size={16} weight="fill" />
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            right: 0,
                                            p: '4px 6px',
                                            color: '#fff',
                                            fontSize: '12px',
                                            borderRadius: '10px 0 0 0',
                                        }}
                                    >
                                        {durations[idx] && formatTime(durations[idx])}
                                    </Box>
                                </>
                            ) : (
                                <img
                                    src={getUrl(src)}
                                    alt=""
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                    loading="lazy"
                                />
                            )}
                        </Box>
                    )
                })}
            </Box>

            <Modal
                open={modalOpen}
                onClick={(e) => e.stopPropagation()}
                onClose={(e) => {
                    e.stopPropagation()
                    handleClose()
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    visibility: isPip ? 'hidden' : 'visible',
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.84)',
                            backdropFilter: 'blur(16px)',
                            transition: 'all 0.4s ease',
                        },
                    },
                }}
            >
                <Sheet
                    onClick={(e) => {
                        e.stopPropagation()
                        // Sheet растянут на весь экран и лежит поверх backdrop'а,
                        // поэтому штатное закрытие MUI по клику вне окна до него
                        // не доходило: кликом «по фону» здесь считается клик мимо
                        // самого плеера и кнопки закрытия.
                        if (e.target.closest('.player-container')) return
                        handleClose()
                    }}
                    sx={{
                        bgcolor: 'transparent !important',
                        boxShadow: 'none',
                        outline: 'none',
                        width: '100vw',
                        height: '100dvh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: `${isClosing ? fadeOut : popIn} 0.3s ease-out forwards`,
                    }}
                >
                    <ModalClose
                        onClick={handleClose}
                        sx={{
                            position: 'fixed',
                            top: '24px',
                            right: '24px',
                            width: '48px',
                            height: '48px',
                            // Иконка внутри ModalClose не тянется за размером
                            // кнопки сама — задаём явно, иначе крестик потеряется
                            // в увеличенном круге.
                            '--Icon-fontSize': '24px',
                            color: '#fff',
                            bgcolor: 'rgba(255,255,255,0.12)',
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter: 'blur(12px)',
                            border: '1px solid rgba(255,255,255,0.07)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
                            zIndex: 2000,
                            borderRadius: '50%',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                        }}
                    />
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 1, sm: 2, md: 3 },
                        }}
                    >
                        {isCurrentVideo && (
                            <VideoPlayer
                                src={currentSrc}
                                variant="modal"
                                autoPlay
                                onPipChange={handlePip}
                            />
                        )}
                    </Box>
                </Sheet>
            </Modal>
        </>
    )
}

export default Attachments
